/**
 * Hook for fetching personal hours summary (current month).
 * Sprint 5, SDD §7.4 — FR-REPORT-08.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';

// ── Types ────────────────────────────────────────────────────────────────

interface CategorySummary {
  category: string;
  entry_count: number;
  total_minutes: number;
  total_hours: number;
}

export interface HoursSummaryData {
  period: {
    start: string;
    end: string;
    label: string;
  };
  categories: CategorySummary[];
  total_minutes: number;
  total_hours: number;
  total_entries: number;
}

export interface UseHoursSummaryReturn {
  data: HoursSummaryData | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

// ── Hook ─────────────────────────────────────────────────────────────────

export function useHoursSummary(): UseHoursSummaryReturn {
  const [data, setData] = useState<HoursSummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFetch<HoursSummaryData>('/api/punch/summary');
      setData(result);
    } catch {
      setError('Failed to load hours summary');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { data, loading, error, refresh };
}
